/**
 * OpsKnowledgePage — list, filter and manage all Knowledge Center articles.
 * Executive Director only. Route: /ops/knowledge
 */
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import OpsLayout from './OpsLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/knowledge`;
const kcApi = axios.create({ baseURL: API, withCredentials: true });

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('ar-SA'); } catch { return iso; }
};

const OpsKnowledgePage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState('');
  const [showCatManager, setShowCatManager] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab !== 'all') params.status = tab;
      if (q) params.q = q;
      const [a, c] = await Promise.all([
        kcApi.get('/admin/articles', { params }),
        kcApi.get('/admin/categories'),
      ]);
      setItems(a.data.items || []);
      setCats(c.data.categories || []);
    } finally {
      setLoading(false);
    }
  }, [tab, q]);

  useEffect(() => { refresh(); }, [refresh]);

  const flash = (msg) => { setBanner(msg); setTimeout(() => setBanner(''), 2500); };

  const del = async (id) => {
    if (!window.confirm('هل تريد حذف هذا المقال نهائياً؟')) return;
    await kcApi.delete(`/admin/articles/${id}`);
    flash('تم الحذف');
    refresh();
  };

  const toggleFeatured = async (id) => {
    await kcApi.post(`/admin/articles/${id}/feature`);
    flash('تم التحديث');
    refresh();
  };

  return (
    <OpsLayout title="مركز المعرفة" subtitle="إدارة المقالات والتصنيفات">
      {banner && (
        <div className="mb-4 bg-[#EBF3E4] border border-[#C4D8AA] text-[#1F3D2B] rounded-lg px-4 py-3 text-sm">
          {banner}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2 bg-white rounded-lg border border-[#E6E0D6] p-1">
          {[
            { k: 'all', label: 'الكل' },
            { k: 'published', label: 'المنشورة' },
            { k: 'draft', label: 'المسودات' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`kc-tab-${t.k}`}
              className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                tab === t.k ? 'bg-[#1F3D2B] text-white' : 'text-[#3D512D] hover:bg-[#F3F1E9]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="بحث..."
            data-testid="kc-admin-search"
            className="bg-white border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#4A641F]"
          />
          <button
            onClick={() => setShowCatManager(true)}
            data-testid="kc-manage-cats-btn"
            className="border border-[#1F3D2B] text-[#1F3D2B] hover:bg-[#F3F1E9] rounded-lg px-4 py-2 text-sm font-medium"
          >
            إدارة التصنيفات
          </button>
          <button
            onClick={() => navigate('/ops/knowledge/new')}
            data-testid="kc-new-article-btn"
            className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-lg px-4 py-2 text-sm font-bold"
          >
            + مقال جديد
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E6E0D6] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F5F2EC] text-[#1F3D2B]">
              <th className="text-right px-4 py-3 font-bold">العنوان</th>
              <th className="text-right px-4 py-3 font-bold hidden md:table-cell">التصنيف</th>
              <th className="text-right px-4 py-3 font-bold hidden md:table-cell">النشر</th>
              <th className="text-right px-4 py-3 font-bold">الحالة</th>
              <th className="text-right px-4 py-3 font-bold">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-8 text-center text-[#7A866E]">جارٍ التحميل...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[#7A866E]">لا توجد مقالات بعد</td></tr>
            )}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-[#F0EBE2]" data-testid={`kc-row-${a.id}`}>
                <td className="px-4 py-3">
                  <div className="font-bold text-[#1F3D2B]">{a.title}</div>
                  <div className="text-[11px] text-[#7A866E] mt-0.5">/{a.slug}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-[#3D512D]">{a.category_name_ar || '—'}</td>
                <td className="px-4 py-3 hidden md:table-cell text-[#7A866E]">{fmt(a.published_at)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex text-[11px] font-bold px-2 py-1 rounded-full ${
                    a.status === 'published'
                      ? 'bg-[#EBF3E4] text-[#1F3D2B]'
                      : 'bg-[#F5EFE0] text-[#8B6F3E]'
                  }`}>
                    {a.status === 'published' ? 'منشور' : 'مسودة'}
                  </span>
                  {a.featured && (
                    <span className="mr-2 inline-flex text-[11px] bg-[#F5EFE0] text-[#8B6F3E] px-2 py-1 rounded-full">
                      مميز
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 text-[12px]">
                    <Link
                      to={`/ops/knowledge/${a.id}`}
                      className="text-[#1F3D2B] font-bold border-b border-[#B08A50] hover:border-[#1F3D2B]"
                    >
                      تعديل
                    </Link>
                    {a.status === 'published' && (
                      <>
                        <span className="text-[#E6E0D6]">|</span>
                        <button
                          onClick={() => toggleFeatured(a.id)}
                          className="text-[#8B6F3E] hover:text-[#1F3D2B]"
                        >
                          {a.featured ? 'إلغاء التمييز' : 'تمييز'}
                        </button>
                        <span className="text-[#E6E0D6]">|</span>
                        <Link
                          to={`/knowledge/${a.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#4A641F] hover:text-[#1F3D2B]"
                        >
                          معاينة
                        </Link>
                      </>
                    )}
                    <span className="text-[#E6E0D6]">|</span>
                    <button
                      onClick={() => del(a.id)}
                      className="text-[#B02B2B] hover:underline"
                    >
                      حذف
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCatManager && (
        <CategoryManager
          cats={cats}
          onClose={() => { setShowCatManager(false); refresh(); }}
        />
      )}
    </OpsLayout>
  );
};

// ── Category CRUD modal ──────────────────────────────────────────────
const CategoryManager = ({ cats, onClose }) => {
  const [rows, setRows] = useState(cats);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true); setErr('');
    try {
      const { data } = await kcApi.post('/admin/categories', {
        name_ar: newName.trim(),
        order: rows.length,
      });
      setRows([...rows, data]);
      setNewName('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'تعذّرت الإضافة');
    } finally {
      setBusy(false);
    }
  };

  const save = async (c) => {
    setBusy(true); setErr('');
    try {
      const { data } = await kcApi.put(`/admin/categories/${c.id}`, {
        name_ar: c.name_ar,
        name_en: c.name_en,
        order: c.order,
      });
      setRows(rows.map((r) => (r.id === c.id ? data : r)));
    } catch (e) {
      setErr(e.response?.data?.detail || 'تعذّر الحفظ');
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm('هل تريد حذف هذا التصنيف؟')) return;
    setBusy(true); setErr('');
    try {
      await kcApi.delete(`/admin/categories/${id}`);
      setRows(rows.filter((r) => r.id !== id));
    } catch (e) {
      setErr(e.response?.data?.detail || 'تعذّر الحذف');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      dir="rtl"
      className="fixed inset-0 bg-[#1F3D2B]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="kc-cat-manager"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#1F3D2B] text-lg">إدارة التصنيفات</h3>
          <button onClick={onClose} className="text-[#7A866E] hover:text-[#1F3D2B]">✕</button>
        </div>
        {err && <div className="bg-[#FBEBEB] text-[#B02B2B] rounded p-2 text-sm mb-3">{err}</div>}

        <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
          {rows.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-[#E6E0D6] rounded-lg p-2">
              <input
                value={c.name_ar}
                onChange={(e) => setRows(rows.map((r) => r.id === c.id ? { ...r, name_ar: e.target.value } : r))}
                className="flex-1 text-sm border-0 focus:outline-none bg-transparent"
              />
              <button onClick={() => save(c)} disabled={busy} className="text-[12px] text-[#1F3D2B] font-bold px-2">حفظ</button>
              <button onClick={() => del(c.id)} disabled={busy} className="text-[12px] text-[#B02B2B] px-2">حذف</button>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-3 border-t border-[#F0EBE2]">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="اسم تصنيف جديد"
            className="flex-1 border border-[#E6E0D6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4A641F]"
          />
          <button onClick={add} disabled={busy} className="bg-[#1F3D2B] text-white rounded-lg px-4 py-2 text-sm font-bold">
            إضافة
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpsKnowledgePage;
